import React, { useEffect, useState, useMemo } from "react";
import { CheckCircle2, FileSignature } from "lucide-react";
import { Modal } from "./Modal";
import { SignaturePad } from "./SignaturePad";
import { useAuth } from "../context/AuthContext";
import api, { apiErr } from "../lib/api";
import { toast } from "sonner";

/**
 * Renders a form (defined by `form.schema`) for the customer to fill, sign and
 * submit. If `existingSubmission` is supplied, the form opens in read-only mode
 * with a "Re-sign" button that switches back to edit mode.
 */
export function FormFiller({ form, existingSubmission, onClose, onSubmitted }) {
  const { user } = useAuth();
  const [readonly, setReadonly] = useState(!!existingSubmission);
  const [data, setData] = useState(() => {
    if (existingSubmission?.data) return { ...existingSubmission.data };
    const init = {};
    for (const f of form.schema || []) {
      if (f.section) continue;
      if (f.default !== undefined) init[f.key] = f.default;
      else init[f.key] = "";
    }
    return init;
  });
  const [sig, setSig] = useState({ name: existingSubmission?.signature_name || user?.name || "", dataurl: null });
  const [busy, setBusy] = useState(false);
  const [customer, setCustomer] = useState(null);

  // Prefill from customer doc on first open (read-only or fresh)
  useEffect(() => {
    let cancel = false;
    async function load() {
      if (!user?.customer_id) return;
      try {
        const r = await api.get(`/customers/${user.customer_id}`);
        if (cancel) return;
        setCustomer(r.data);
        if (!existingSubmission) {
          setData((d) => {
            const next = { ...d };
            for (const f of form.schema || []) {
              if (!f.prefill) continue;
              const val = r.data?.[f.prefill] || "";
              if (val && !next[f.key]) next[f.key] = val;
            }
            return next;
          });
        }
      } catch (e) { /* non-fatal */ }
    }
    load();
    return () => { cancel = true; };
  }, [user?.customer_id, existingSubmission, form.schema]);

  const visibleFields = useMemo(() => {
    return (form.schema || []).filter((f) => {
      if (f.section) return true;
      if (!f.show_if) return true;
      return data[f.show_if.key] === f.show_if.equals;
    });
  }, [form.schema, data]);

  const setVal = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const submit = async () => {
    // Validate required fields
    for (const f of visibleFields) {
      if (f.section) continue;
      if (f.required && !String(data[f.key] || "").trim()) {
        toast.error(`${f.label} is required`);
        return;
      }
    }
    if (!sig.name || !sig.name.trim()) {
      toast.error("Please enter your name in the signature box");
      return;
    }
    if (!sig.dataurl) {
      toast.error("Please draw your signature, or switch to 'Type' mode");
      return;
    }
    setBusy(true);
    try {
      await api.post("/portal/form-submissions", {
        form_id: form.id,
        data,
        signature_name: sig.name,
        signature_dataurl: sig.dataurl,
        signature_date: new Date().toISOString().slice(0, 10),
      });
      onSubmitted();
    } catch (e) {
      toast.error(apiErr(e));
    } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} wide
      icon={readonly ? CheckCircle2 : FileSignature}
      title={form.title}
      subtitle={`${form.code || ""}${form.version ? ` · Revision ${form.version}` : ""}${form.revision_date ? ` · ${form.revision_date}` : ""}`}
      footer={readonly ? (
        <>
          <button className="btn ghost" onClick={onClose}>Close</button>
          <button className="btn gold" onClick={() => setReadonly(false)} data-testid="form-resign-btn">Re-sign form</button>
        </>
      ) : (
        <>
          <button className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn gold" onClick={submit} disabled={busy} data-testid="form-submit-btn">
            {busy ? "Submitting…" : "Sign & submit"}
          </button>
        </>
      )}
    >
      {form.description && (
        <div className="card pad" style={{ background: "var(--card-2)", marginBottom: 16, fontSize: 13, lineHeight: 1.55 }}>
          {form.description}
        </div>
      )}

      {existingSubmission && readonly && (
        <div className="card pad" style={{ background: "#dff6e3", border: "1px solid #b5e0c0", marginBottom: 16, fontSize: 13 }}>
          <strong>Already signed</strong> by <strong>{existingSubmission.signature_name}</strong> on {existingSubmission.submitted_at?.slice(0, 10)}. Click <em>Re-sign form</em> to submit a new version.
        </div>
      )}

      <div style={{ display: "grid", gap: 14 }}>
        {visibleFields.map((f, idx) => {
          if (f.section) {
            return (
              <div key={`sec-${idx}`} style={{ marginTop: idx === 0 ? 0 : 14, paddingBottom: 6, borderBottom: "1px solid var(--border)" }}>
                <div className="t-strong" style={{ fontSize: 13.5, letterSpacing: ".03em", textTransform: "uppercase", color: "var(--gold-deep)" }}>{f.section}</div>
              </div>
            );
          }
          const common = {
            className: "input",
            value: data[f.key] ?? "",
            onChange: (e) => setVal(f.key, e.target.value),
            disabled: readonly,
            "data-testid": `field-${f.key}`,
          };
          return (
            <div key={f.key} className="fld">
              <label>{f.label}{f.required && <span className="req"> *</span>}</label>
              {f.type === "textarea" && <textarea rows={3} {...common} />}
              {f.type === "tel" && <input type="tel" {...common} />}
              {f.type === "email" && <input type="email" {...common} />}
              {f.type === "radio" && (
                <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 4 }}>
                  {f.options.map((o) => (
                    <label key={o.value} style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                      <input type="radio" name={f.key} value={o.value} checked={data[f.key] === o.value}
                        onChange={(e) => setVal(f.key, e.target.value)} disabled={readonly}
                        data-testid={`field-${f.key}-${o.value}`} />
                      {o.label}
                    </label>
                  ))}
                </div>
              )}
              {(!f.type || f.type === "text") && <input type="text" {...common} />}
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 22 }}>
        <div className="t-strong" style={{ fontSize: 13.5, letterSpacing: ".03em", textTransform: "uppercase", color: "var(--gold-deep)", marginBottom: 8, paddingBottom: 6, borderBottom: "1px solid var(--border)" }}>
          Signature
        </div>
        {readonly && existingSubmission?.has_signature_image ? (
          <SignatureViewer submissionId={existingSubmission.id} signedBy={existingSubmission.signature_name} signedAt={existingSubmission.submitted_at} />
        ) : (
          <>
            <p className="s t-mut" style={{ marginBottom: 10 }}>By signing, you confirm acceptance of PTE&apos;s quotation and authorise us to proceed with the calibration.</p>
            <SignaturePad defaultName={sig.name} onChange={(v) => setSig(v)} />
          </>
        )}
      </div>
    </Modal>
  );
}

function SignatureViewer({ submissionId, signedBy, signedAt }) {
  const [sigUrl, setSigUrl] = useState(null);
  useEffect(() => {
    api.get(`/form-submissions/${submissionId}`).then((r) => setSigUrl(r.data.signature_dataurl)).catch(() => {});
  }, [submissionId]);
  return (
    <div style={{ border: "1px solid var(--border)", borderRadius: 8, padding: 14, background: "#fff" }}>
      <div style={{ minHeight: 120, display: "flex", alignItems: "center", justifyContent: "flex-start" }}>
        {sigUrl ? <img src={sigUrl} alt="signature" style={{ maxHeight: 110, maxWidth: "100%" }} data-testid="sig-image" /> : <span className="t-mut">Loading signature…</span>}
      </div>
      <div className="s t-mut" style={{ marginTop: 6 }}>Signed by <strong>{signedBy}</strong> on {signedAt?.slice(0, 10)}</div>
    </div>
  );
}
